import { useRef } from 'react';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { generatePath, useLocation, useParams } from 'react-router-dom';

import { useAuthentication } from '@zenbusiness/application-commons-authentication';
import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useLinkPressedCallback } from '@zenbusiness/application-commons-clickstream';
import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import Empty from '@zenbusiness/zui/Empty';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Text from '@zenbusiness/zui/Text';

import { isEntityUrl } from '#client/constants/routes';
import * as ROUTES from '#client/constants/routes';
import { buildCartUrl, generateZenUrl } from '#client/utils/urls';

import * as Styled from './BusinessEntitySelector.style';
import { EntityMenuItem } from './components/EntityMenuItem';
import { MenuItem } from './components/MenuItem';
import useBusinessEntitySelector from './hooks/useBusinessEntitySelector';
import useAnchorElement from '../../hooks/useAnchorElement';

const ctaText = {
  addBusiness: 'Add new business',
  formBusiness: 'Form a new business',
  reserveBusiness: 'Reserve a Business Name',
  viewBusinesses: 'View all companies'
};

const anchorEventProps = {
  elementName: 'entity_selector_button'
};
const baseEventProps = {
  elementName: 'entity_selector_option',
  navLevel: 1
};

function BusinessEntitySelector() {
  const entityMenuRef = useRef(null);
  const buttonRef = useRef(null);
  const enableBusinessNameReservation = useExperimentFlag(
    'enable-business-name-reservation',
    false
  );
  const { authenticated } = useAuthentication();
  const { pathname } = useLocation();
  const currentParams = useParams();
  const { loading, currentEntity = {}, businessEntities = [] } = useBusinessEntitySelector();
  const { loading: businessEntityLoading } = useBusinessEntity();
  const { anchorElement, onButtonClick, onMenuClose } = useAnchorElement(
    entityMenuRef,
    buttonRef,
    anchorEventProps
  );
  const newCompanyLink = '/formation-types-compare/';
  const reserveNameLink = buildCartUrl('entity_name_reservation', {}, { required: [] });
  const viewBusinessesLink = generatePath(ROUTES.entities);

  const newCompanyText = businessEntities.length === 0 ? ctaText.formBusiness : ctaText.addBusiness;
  const isNonEntityPage = !isEntityUrl(pathname);
  const hasMultipleEntities = businessEntities.length > 1;

  const handleClickNew = useLinkPressedCallback({
    ...baseEventProps,
    elementText: newCompanyText,
    destination: generateZenUrl(newCompanyLink)
  });

  const handleClickReserve = useLinkPressedCallback({
    ...baseEventProps,
    elementText: ctaText.reserveBusiness,
    destination: reserveNameLink
  });

  const handleTrackingViewBusinesses = useLinkPressedCallback({
    ...baseEventProps,
    elementText: ctaText.viewBusinesses,
    destination: generateZenUrl(viewBusinessesLink)
  });

  const handleClickViewBusinesses = () => {
    handleTrackingViewBusinesses();
    onMenuClose();
  };

  const ExpandIcon = anchorElement ? ExpandLessIcon : ExpandMoreIcon;

  if (!authenticated || isNonEntityPage) {
    return <Empty isNonEntityPage={isNonEntityPage} authenticated={authenticated} />;
  }

  if (loading || businessEntityLoading) {
    return <Styled.Skeleton>Loading</Styled.Skeleton>;
  }

  return (
    <>
      <Styled.Button
        aria-controls="entity-menu"
        aria-haspopup="menu"
        aria-label="entity menu"
        icon={<ExpandIcon aria-hidden />}
        iconPosition="end"
        onClick={onButtonClick}
        open={anchorElement}
        ref={buttonRef}
        variant="contained"
      >
        <Text truncate size="md">{currentEntity.name}</Text>
      </Styled.Button>

      <Styled.Menu
        anchorEl={anchorElement}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        aria-expanded={Boolean(anchorElement)}
        elevation={0}
        id="entity-menu"
        keepMounted
        onClose={onMenuClose}
        open={Boolean(anchorElement)}
        ref={entityMenuRef}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
      >
        {hasMultipleEntities
          && businessEntities.map((entity, idx) => {
            // Only requires checking businessEntityUuid param in url
            const entityPath = currentParams.businessEntityUuid
              ? pathname.replace(currentParams.businessEntityUuid, entity.uuid)
              : `/dashboard/${entity.uuid}`;

            const entityItem = {
              ...entity,
              eventProps: {
                ...baseEventProps,
                elementText: entity.name,
                destination: entityPath,
                navIndex: idx + 1
              },
              path: entityPath
            };

            return (
              <EntityMenuItem
                entity={entityItem}
                key={entityItem.uuid}
                onClick={onMenuClose}
              />
            );
          })}

        {hasMultipleEntities && (
          <MenuItem
            onClick={handleClickViewBusinesses}
            variant="view-all"
          >
            <Styled.InternalLink
              end
              role="menuitem"
              to={viewBusinessesLink}
            >
              <span>{ctaText.viewBusinesses}</span>
            </Styled.InternalLink>
          </MenuItem>
        )}

        {hasMultipleEntities && <Styled.Divider />}

        <MenuItem onClick={handleClickNew}>
          <Styled.ExternalLink
            href={newCompanyLink}
            role="menuitem"
          >
            <SharedAsset
              alt=""
              aria-hidden
              src="/icon/micro/line-plus-thin.svg"
              width={16}
            />
            <span>{newCompanyText}</span>
          </Styled.ExternalLink>
        </MenuItem>

        {enableBusinessNameReservation && (
          <MenuItem onClick={handleClickReserve}>
            <Styled.ExternalLink
              href={reserveNameLink}
              role="menuitem"
            >
              <SharedAsset
                alt=""
                src="/icon/micro/line-label.svg"
                width={16}
              />
              <span>{ctaText.reserveBusiness}</span>
            </Styled.ExternalLink>
          </MenuItem>
        )}
      </Styled.Menu>
    </>
  );
}

BusinessEntitySelector.propTypes = {};

export default BusinessEntitySelector;
